import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';

const Sports = () => {
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedSport, setSelectedSport] = useState('all');

  useEffect(() => {
    fetchSports();
  }, []);

  const fetchSports = async () => {
    try {
      const response = await fetch('/admin/api/sports.php');
      const data = await response.json();
      setSports(data);
    } catch (error) {
      console.error('Error fetching sports:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSports = sports.filter(sport => {
    const typeMatch = selectedType === 'all' || sport.type === selectedType;
    const sportMatch = selectedSport === 'all' || sport.sport === selectedSport;
    return typeMatch && sportMatch;
  });

  const uniqueTypes = [...new Set(sports.map(sport => sport.type))];
  const uniqueSports = [...new Set(sports.map(sport => sport.sport))];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Sports Corner</h1>
            <p className="text-xl text-orange-100 max-w-3xl mx-auto">
              Stay updated with our sports events, achievements, and team activities. 
              Celebrate our athletes' victories and join us in supporting school sports.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="all">All Types</option>
                {uniqueTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Sport
              </label>
              <select
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="all">All Sports</option>
                {uniqueSports.map(sport => (
                  <option key={sport} value={sport}>{sport}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Sports Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {filteredSports.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="text-gray-400 text-6xl mb-4">
                <i className="fas fa-trophy"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Sports Events Found</h3>
              <p className="text-gray-600">No sports events match your current filters.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSports.map((sport) => (
              <div
                key={sport.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300"
              >
                {sport.image_path && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={`admin/${sport.image_path}`}
                      alt={sport.event_name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      sport.type === 'achievement' 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : sport.type === 'event'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {sport.type}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatDate(sport.date)}
                    </span>
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {sport.event_name}
                  </h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-600">
                      <i className="fas fa-futbol w-4 h-4 mr-2"></i>
                      <span className="text-sm">{sport.sport}</span>
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <i className="fas fa-map-marker-alt w-4 h-4 mr-2"></i>
                      <span className="text-sm">{sport.location}</span>
                    </div>
                    
                    {sport.results && (
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-medal w-4 h-4 mr-2"></i>
                        <span className="text-sm">{sport.results}</span>
                      </div>
                    )}
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {sport.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      <i className="fas fa-calendar w-4 h-4 mr-1"></i>
                      {formatDate(sport.date)}
                    </div>
                    
                    <button className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors">
                      <i className="fas fa-eye mr-1"></i>View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sports Achievements Summary */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <i className="fas fa-trophy text-orange-600 text-xl"></i>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-orange-900 mb-2">
                Sports Achievements
              </h3>
              <p className="text-orange-800 mb-4">
                Our school has a proud tradition of excellence in sports. We encourage all students 
                to participate in various sporting activities to develop physical fitness, teamwork, 
                and competitive spirit.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">120+</div>
                  <div className="text-sm text-orange-700">Sports Events</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">15+</div>
                  <div className="text-sm text-orange-700">Different Sports</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">200+</div>
                  <div className="text-sm text-orange-700">Student Athletes</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Sports;
